import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils"; // Adjust the import path as necessary
import { sub } from "date-fns";
import { ChevronDown, Check } from "lucide-react";

const ResignationForm = ({
  loading,
  closeModal,
  onSubmit,
  initialValues,
  errors = {}, // Added errors prop to display validation messages
}) => {
  const [formData, setFormData] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const token = getToken();
  const [users, setUsers] = useState([]);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const employeeDropdownRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Real-time validation
    if (name === "notice_period_days") {
      const numValue = value === "" ? "" : parseInt(value) || "";
      let error = "";
      if (!numValue && numValue !== 0) {
        error = "Notice Period Days is required.";
      } else if (numValue < 0) {
        error = "Notice Period Days must be 0 or greater.";
      } else if (numValue.toString().length > 4) {
        error = "Notice Period Days must not exceed 4 digits.";
      }
      
      if (error) {
        setFormErrors((prev) => ({ ...prev, notice_period_days: error }));
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.notice_period_days;
          return newErrors;
        });
      }
    } else if (name === "reason") {
      let error = "";
      if (!value.trim()) {
        error = "Reason is required.";
      } else if (value.length > 300) {
        error = "Reason must not exceed 300 characters.";
      }
      
      if (error) {
        setFormErrors((prev) => ({ ...prev, reason: error }));
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.reason;
          return newErrors;
        });
      }
    }
  };

  // fetching user on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`https://hrmspayroll.prepstripe.com/api/users-with-employee-ids`, {
          headers: {
            contentType: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        setUsers(res.data);
      } catch (error) {
        console.error("Failed to fetch users:", error);
      }
    };

    fetchUsers();
  }, [token]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(e.target)) {
        setIsEmployeeDropdownOpen(false);
        // Clear search query if we have a selected value
        if (formData.employee_id) {
          setEmployeeSearchQuery("");
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [formData.employee_id]);

  // Filter users based on search query
  const filteredUsers = employeeSearchQuery === ""
    ? users
    : users.filter((user) =>
        `${user.employee_id} - ${user.name}`.toLowerCase().includes(employeeSearchQuery.toLowerCase())
      );

  // Get selected user display text
  const selectedUser = users.find((user) => user.employee_id === formData.employee_id);
  const selectedUserDisplay = selectedUser
    ? `${selectedUser.employee_id} - ${selectedUser.name}`
    : "";

  // Display value: show search query if dropdown is open and searching, otherwise show selected user
  const displayValue = isEmployeeDropdownOpen && employeeSearchQuery 
    ? employeeSearchQuery 
    : (selectedUserDisplay || employeeSearchQuery || "");

  const validate = () => {
    const validationErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!formData.employee_id) {
      validationErrors.employee_id = "Employee ID is required.";
    }

    if (!formData.notice_period_days && formData.notice_period_days !== 0) {
      validationErrors.notice_period_days = "Notice Period Days is required.";
    } else if (formData.notice_period_days.toString().length > 4) {
      validationErrors.notice_period_days = "Notice Period Days must not exceed 4 digits.";
    }

    if (!formData.reason || !formData.reason.trim()) {
      validationErrors.reason = "Reason is required.";
    } else if (formData.reason.length > 300) {
      validationErrors.reason = "Reason must not exceed 300 characters.";
    }

    return validationErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }	

    setFormErrors("")

    const submissionData = new FormData(); // Use FormData for file upload
    submissionData.append("employee_id", formData.employee_id);
    submissionData.append("resignation_date", formData.resignation_date);
    submissionData.append("last_working_day", formData.last_working_day);
    submissionData.append("notice_period_days", formData.notice_period_days);
    submissionData.append("reason", formData.reason);

    onSubmit(submissionData); // Pass the FormData to the parent onSubmit handler
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 font-poppins">

      {/* employee_id Field - Searchable Dropdown */}
      <div ref={employeeDropdownRef}>
        <label htmlFor="employee_id" className="block dark:text-white text-sm font-medium text-gray-700">
          Employee ID <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="relative mt-1">
          <input
            type="text"
            value={displayValue}
            onChange={(e) => {
              setEmployeeSearchQuery(e.target.value);
              setIsEmployeeDropdownOpen(true);
              // Clear selection if user starts typing
              if (e.target.value && formData.employee_id) {
                setFormData((prev) => ({ ...prev, employee_id: "" }));
              }
            }}
            onClick={() => {
              setIsEmployeeDropdownOpen(true);
              if (!isEmployeeDropdownOpen) {
                setEmployeeSearchQuery("");
              }
            }}
            placeholder="Search employee by ID or name..."
            className={`w-full p-2 border rounded-md dark:bg-black dark:text-white dark:border-gray-700 pr-10 ${
              formErrors.employee_id ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          <ChevronDown
            className="absolute right-2 top-2.5 text-gray-400 pointer-events-none"
            size={18}
          />
          {isEmployeeDropdownOpen && (
            <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
              {filteredUsers.length === 0 ? (
                <li className="px-3 py-2 text-gray-500 dark:text-gray-300">
                  No employee found
                </li>
              ) : (
                filteredUsers.map((user) => (
                  <li
                    key={user.id}
                    className={`px-3 py-2 cursor-pointer flex items-center justify-between ${
                      formData.employee_id === user.employee_id
                        ? "bg-blue-100 dark:bg-blue-600 text-blue-700 dark:text-white"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, employee_id: user.employee_id }));
                      setEmployeeSearchQuery("");
                      setIsEmployeeDropdownOpen(false);
                      // Clear error if exists
                      setFormErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.employee_id;
                        return newErrors;
                      });
                    }}
                  >
                    {user.employee_id} - {user.name}
                    {formData.employee_id === user.employee_id && <Check size={16} />}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
        {formErrors.employee_id && <p className="text-red-500 text-sm mt-1">{formErrors.employee_id}</p>}
      </div>

      {/* resignation_date Field */}
      <div>
        <label htmlFor="resignation_date" className="block dark:text-white text-sm font-medium text-gray-700">
          Resignation Date <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="date"
          id="resignation_date"
          name="resignation_date"
          value={formData.resignation_date}
          onChange={handleChange}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
            formErrors.resignation_date ? "border-red-500" : "border-gray-300"
          }`}
          required
        />
        {formErrors.resignation_date && <p className="text-red-500 text-sm mt-1">{formErrors.resignation_date}</p>}
      </div>

      {/* resignation_date Field */}
      <div>
        <label htmlFor="last_working_day" className="block dark:text-white text-sm font-medium text-gray-700">
          Last Working Day <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="date"
          id="last_working_day"
          name="last_working_day"
          value={formData.last_working_day}
          onChange={handleChange}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
            formErrors.last_working_day ? "border-red-500" : "border-gray-300"
          }`}
          required
        />
        {formErrors.last_working_day && <p className="text-red-500 text-sm mt-1">{formErrors.last_working_day}</p>}
      </div>

      <div>
        <label htmlFor="notice_period_days" className="block dark:text-white text-sm font-medium text-gray-700">
          Notice Period Days <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="number"
          id="notice_period_days"
          name="notice_period_days"
          value={formData.notice_period_days || ""}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, ""); // Remove non-digit characters
            const numValue = value === "" ? "" : parseInt(value) || "";
            // Only update if 4 digits or less
            if (value.length <= 4) {
              setFormData((prev) => ({ ...prev, notice_period_days: numValue }));
            }
            
            // Real-time validation
            let error = "";
            if (!numValue && numValue !== 0) {
              error = "Notice Period Days is required.";
            } else if (numValue < 0) {
              error = "Notice Period Days must be 0 or greater.";
            } else if (value.length > 4) {
              error = "Notice Period Days must not exceed 4 digits.";
            }
            
            if (error) {
              setFormErrors((prev) => ({ ...prev, notice_period_days: error }));
            } else {
              setFormErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.notice_period_days;
                return newErrors;
              });
            }
          }}
          min="0"
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
            formErrors.notice_period_days ? "border-red-500" : "border-gray-300"
          }`}
          required
        />
        {formErrors.notice_period_days && <p className="text-red-500 text-sm mt-1">{formErrors.notice_period_days}</p>}
      </div>

      <div>
        <label htmlFor="reason" className="block dark:text-white text-sm font-medium text-gray-700">
          Reason <span className="text-red-500 ml-1">*</span>
        </label>
        <textarea
          id="reason"
          name="reason"
          value={formData.reason || ""}
          onChange={handleChange}
          rows={4}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
            formErrors.reason ? "border-red-500" : "border-gray-300"
          }`}
          required
        />
        <div className="flex justify-between items-center mt-1">
          {formErrors.reason && <p className="text-red-500 text-sm">{formErrors.reason}</p>}
          <p className={`text-sm ml-auto ${(formData.reason?.length || 0) > 300 ? "text-red-500" : "text-gray-500"}`}>
            {(formData.reason?.length || 0)} / 300
          </p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-2 items-center">
        <button
          type="button"
          onClick={closeModal}
          className="px-5 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex items-center gap-2 px-9 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {initialValues.id ? "Update" : "Add"}
          {loading && (
            <svg
              className="animate-spin h-5 w-5 ml-2 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          )}
        </button>
      </div>
    </form>
  );
};

export default ResignationForm;
